/*
 * MenuPage - initializes interactive components on Menu page, that is:
 * - Menu dropdowns and quantity badges
 * - Cart
 * - Product Card
 *
 * These components require common data (restaurant menu, ordering availability), therefore are initialized together.
 *
 * We detect if we are on Menu page by the presence of a `[data-cart]` element.
 */
import * as CartIntegration from "../../../output/Restaumatic.Site.Components.Cart.Integration/index.js";
import { formatNumberAsCurrency } from "../../../output/Restaumatic.Site.I18n/index.js";
import { subscribe } from "../../../output/Specular.JS.Dynamic/index.js";
import * as QrWaiter from "../services/QrWaiter";
import { updateModalFromHash } from "../utils/Modals";
import { RestaurantId, Tracking, } from "restaumatic-client";
const apiPath = "/api/v1/sites/rpc?GetOrderingAvailabilityRequest";
function init() {
    if (!document.querySelector("[data-cart]"))
        return; // not in menu page
    const path = document
        .querySelector("[data-cart-menu-path]")
        .getAttribute("data-cart-menu-path");
    const restaurantId = new RestaurantId(parseInt(document
        .querySelector("[data-cart-restaurant-id]")
        .getAttribute("data-cart-restaurant-id")));
    const isQrWaiterOrder = QrWaiter.isQrWaiterOrder();
    Promise.all([
        fetch(path, {
            headers: {
                Accept: "application/json",
                "x-app-version": "site-latest",
            },
        }).then((r) => r.json()),
        fetch(apiPath, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                method: "GetOrderingAvailabilityRequest",
                id: "",
                params: {
                    restaurantId: restaurantId.value,
                    isQrWaiterOrder,
                    // TODO: implement review mode with new client for iOS
                    isIosReviewMode: false,
                },
            }),
        }).then((r) => r.json()),
        QrWaiter.searchInitialDineInLocation(restaurantId),
    ]).then(([response, availabilityResponse, initialDineInLocation]) => {
        const restaurant = response.result;
        if (availabilityResponse.result) {
            initWithData({
                restaurant,
                themeSettings: window.Skubacz.configuration.theme_settings,
                orderingAvailability: availabilityResponse.result,
                isQrWaiterOrder,
                initialDineInLocation,
            });
        }
    });
}
/*
Initialize cart and its external interface.
See `doc/CartInterface.md` for the specification of this with respect to the
rest of the page.
*/
function initWithData(options) {
    console.log("Initializing cart");
    Tracking.trackEvent({ tag: "VisitPage", contents: { page: "Menu" } });
    const restaurant = options.restaurant;
    const menuConsumer = options.isQrWaiterOrder ? "QrWaiter" : "Site";
    const menu = measure("decodeMenu", function () {
        return CartIntegration.decodeMenu(menuConsumer, restaurant);
    });
    if (options.initialDineInLocation != null) {
        QrWaiter.setDineInLocation(options.initialDineInLocation);
    }
    const cart = measure("runCart", function () {
        return CartIntegration.runCart({
            container: document.body,
            restaurant: restaurant,
            menu: menu,
            accountId: Skubacz.configuration.account_id,
            themeSettings: options.themeSettings,
            orderingAvailability: options.orderingAvailability,
            openProductCard: null,
        })();
    });
    measure("subscribe_to_cart", function () {
        subscribe(cart.grandTotal, (grandTotal) => {
            document.querySelectorAll("[data-cart='grandTotal']").forEach((el) => {
                el.textContent = formatNumberAsCurrency(grandTotal);
            });
        });
        subscribe(cart.canOrder, (canOrder) => {
            document.querySelectorAll("[data-cart='canOrder']").forEach((el) => {
                if (canOrder) {
                    el.classList.add("show");
                }
                else {
                    el.classList.add("hide");
                }
            });
        });
        subscribe(cart.itemCount, (cnt) => {
            document.querySelectorAll("[data-cart='item_count']").forEach((el) => {
                el.textContent = cnt.toString();
            });
        });
    });
    Skubacz.CartHooks = {
        restaurant: function () {
            return restaurant;
        },
    };
    document.querySelectorAll(".js-cart-button").forEach((el) => {
        el.addEventListener("click", (e) => {
            e.preventDefault();
            cart.showCart();
        });
    });
    document.querySelectorAll(".js-checkout-shortcut").forEach((el) => {
        el.addEventListener("click", (e) => {
            e.preventDefault();
            cart.submit();
        });
    });
    measure("loaded.restaumatic.cart", function () {
        const loadedCartEvent = new Event("loaded.restaumatic.cart");
        document.dispatchEvent(loadedCartEvent);
    });
    // Show modal if there is one in location hash
    updateModalFromHash();
}
// Execute a function and record a 'measure' performance entry with its duration.
function measure(name, fn) {
    performance.mark(name + ":start");
    const result = fn();
    performance.mark(name + ":end");
    performance.measure(name, name + ":start", name + ":end");
    return result;
}
export default {
    init,
};
